<template>
    <section class="w-full border-t border-nomad-800 py-6 mb-8" aria-labelledby="song-comments-heading">
        <div class="flex flex-wrap items-center justify-between gap-4 mb-5">
            <h2 id="song-comments-heading" class="text-xl font-bold text-white">Comments</h2>
            <div class="inline-flex rounded-lg bg-nomad-900 p-1" aria-label="Sort comments">
                <button
                    type="button"
                    class="rounded-md px-4 py-2 text-sm font-medium transition-colors"
                    :class="sort === 'top' ? 'bg-nomad-700 text-white' : 'text-nomad-300 hover:text-white'"
                    :aria-pressed="sort === 'top'"
                    :disabled="loading"
                    @click="changeSort('top')"
                >
                    Top
                </button>
                <button
                    type="button"
                    class="rounded-md px-4 py-2 text-sm font-medium transition-colors"
                    :class="sort === 'new' ? 'bg-nomad-700 text-white' : 'text-nomad-300 hover:text-white'"
                    :aria-pressed="sort === 'new'"
                    :disabled="loading"
                    @click="changeSort('new')"
                >
                    New
                </button>
            </div>
        </div>

        <p v-if="error" role="alert" class="mb-4 text-sm text-red-400">{{ error }}</p>

        <p v-if="!comments.length && !loading" class="py-6 text-center text-nomad-300">
            No comments yet.
        </p>

        <ul v-else class="divide-y divide-nomad-800">
            <li v-for="comment in comments" :key="comment.id" class="flex gap-3 py-4">
                <img
                    v-if="comment.user?.image || comment.user?.avatar"
                    :src="comment.user.image || comment.user.avatar"
                    :alt="comment.user.name || 'Comment author'"
                    class="h-10 w-10 shrink-0 rounded-full object-cover"
                />
                <div v-else class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-nomad-700 text-sm font-semibold text-nomad-200">
                    {{ comment.user?.name?.charAt(0)?.toUpperCase() || '?' }}
                </div>
                <div class="min-w-0 flex-1">
                    <div class="mb-1 flex flex-wrap items-baseline gap-x-2 gap-y-1">
                        <span class="font-semibold text-nomad-100">{{ comment.user?.name || 'Heesify user' }}</span>
                        <time class="text-xs text-nomad-400">{{ comment.created_at }}</time>
                    </div>
                    <p class="whitespace-pre-wrap break-words text-nomad-200">{{ comment.body }}</p>
                </div>
            </li>
        </ul>

        <div v-if="hasMore" class="mt-5 text-center">
            <button
                type="button"
                class="rounded-md bg-nomad-800 px-5 py-2.5 text-sm font-semibold text-nomad-200 transition-colors hover:bg-nomad-700 disabled:cursor-wait disabled:opacity-60"
                :disabled="loading"
                @click="loadMore"
            >
                {{ loading ? 'Loading...' : 'Load more' }}
            </button>
        </div>
    </section>
</template>

<script setup>
import axios from 'axios';
import { ref } from 'vue';

const props = defineProps({
    songId: { type: [Number, String], required: true },
    initialComments: { type: Array, default: () => [] },
    initialHasMore: { type: Boolean, default: false },
});

const comments = ref([...props.initialComments]);
const sort = ref('top');
const hasMore = ref(props.initialHasMore);
const loading = ref(false);
const error = ref('');

let bufferedComments = [];
let nextApiPage = 1;
let lastApiPage = Number.POSITIVE_INFINITY;
let apiPageLoaded = false;

async function fetchPage(page, selectedSort) {
    const response = await axios.get('/api/app/comments', {
        params: {
            commentable_type: 'songs',
            commentable_id: props.songId,
            sort: selectedSort === 'new' ? 'newest' : 'top',
            page,
        },
    });

    return response.data;
}

async function changeSort(selectedSort) {
    if (selectedSort === sort.value || loading.value) {
        return;
    }

    loading.value = true;
    error.value = '';

    try {
        const result = await fetchPage(1, selectedSort);
        const pageComments = result.data || [];

        sort.value = selectedSort;
        comments.value = pageComments.slice(0, 5);
        bufferedComments = pageComments.slice(5);
        nextApiPage = Number(result.current_page || 1) + 1;
        lastApiPage = Number(result.last_page || 1);
        apiPageLoaded = true;
        hasMore.value = bufferedComments.length > 0 || nextApiPage <= lastApiPage;
    } catch {
        error.value = 'Could not load comments. Please try again.';
    } finally {
        loading.value = false;
    }
}

async function loadMore() {
    if (loading.value || !hasMore.value) {
        return;
    }

    loading.value = true;
    error.value = '';

    try {
        while (bufferedComments.length < 10 && nextApiPage <= lastApiPage) {
            const result = await fetchPage(nextApiPage, sort.value);
            const pageComments = result.data || [];
            nextApiPage = Number(result.current_page || nextApiPage) + 1;
            lastApiPage = Number(result.last_page || lastApiPage);
            apiPageLoaded = true;

            const existingIds = new Set([
                ...comments.value.map((comment) => comment.id),
                ...bufferedComments.map((comment) => comment.id),
            ]);

            bufferedComments.push(...pageComments.filter((comment) => !existingIds.has(comment.id)));
        }

        if (!apiPageLoaded && nextApiPage <= lastApiPage) {
            const result = await fetchPage(nextApiPage, sort.value);
            const pageComments = result.data || [];
            nextApiPage = Number(result.current_page || nextApiPage) + 1;
            lastApiPage = Number(result.last_page || lastApiPage);
            apiPageLoaded = true;

            const existingIds = new Set(comments.value.map((comment) => comment.id));
            bufferedComments.push(...pageComments.filter((comment) => !existingIds.has(comment.id)));
        }

        comments.value.push(...bufferedComments.splice(0, 10));
        hasMore.value = bufferedComments.length > 0 || nextApiPage <= lastApiPage;
    } catch {
        error.value = 'Could not load more comments. Please try again.';
    } finally {
        loading.value = false;
    }
}
</script>